import { AsyncButton, Button, Card, CardContent, CardHeader, CardTitle, Separator } from '@bpinternal/ui-kit-next'
import { Link } from '@tanstack/react-router'
import { uniqBy } from 'lodash'
import { AlertTriangle, FileIcon, Trash, UploadCloud } from 'lucide-react'
import { useState } from 'react'
import { createPortal } from 'react-dom'
import { FileRejection, useDropzone, type Accept } from 'react-dropzone'
import { match } from 'ts-pattern'
import { FileFormatIcons } from '~/components/FileFormatIcons'
import { Icon } from '~/elements'
import { AddKnowledgeSourceFooter } from '~/features/knowledge-bases/components/knowledge-source/AddKnowledgeSource/AddKnowledgeSourceFooter'
import { getContentType, useUploadDocumentSources } from '~/features/knowledge-bases/hooks/useKnowledgeSource'
import { KnowledgeSourceStepProps } from '~/features/knowledge-bases/types'
import { ProgressBarCard } from '~/features/usage/components'
import { formatValueWithUnit } from '~/features/usage/helpers'
import { useUsage } from '~/hooks'
import { cn, formatters } from '~/utils'

type UploadDocumentStepProps = {
  workspaceId: string
  botId: string
  kbId: string
  footerRef: React.RefObject<HTMLDivElement>
} & KnowledgeSourceStepProps

const MAX_FILE_SIZE = 1000 * 1000 * 100 // 100MB
const ACCEPT: Accept = {
  'application/pdf': ['.pdf'],
  'text/html': ['.html'],
  'text/plain': ['.txt'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'text/markdown': ['.md'],
}
const FILE_STORAGE_ALERT_THRESHOLD = 90
const { pdfIcon, htmlIcon, txtIcon, docIcon, docxIcon, mdIcon } = FileFormatIcons

const StorageAlert = ({ totalFileSize, workspaceId }: { totalFileSize: number; workspaceId: string }) => {
  const currentPeriod = new Date().toISOString().split('T')[0]

  const { storage_count: fileStorage } = useUsage({
    workspaceId,
    quotas: ['storage_count'],
    period: currentPeriod,
  })

  const usageValue = totalFileSize + fileStorage.value
  const usagePercentage = (usageValue / fileStorage.quota) * 100

  const component = (
    <ProgressBarCard
      className="flex border-none bg-gray-1 shadow-none"
      title={
        <div className="mb-2 flex flex-row items-center justify-between gap-2">
          <div className="flex items-center gap-4">
            <p>File Storage almost full</p>
          </div>
          <Link to={'/workspaces/$workspaceId/billing'} params={{ workspaceId }}>
            <Button variant="outline" className="hidden bg-gray-1 shadow-none md:block">
              Add Storage
            </Button>
          </Link>
        </div>
      }
      value={usagePercentage}
    >
      <p className="text-sm text-muted-foreground">
        After this upload, you&apos;ll be using {formatters.number(usagePercentage, 0)}% of your storage. Upgrade now to
        avoid running out of space.
      </p>
      <div className="mb-3 flex justify-between text-sm text-muted-foreground">
        <p>
          {formatValueWithUnit(usageValue, 'storage_count')} / {formatValueWithUnit(fileStorage.quota, 'storage_count')}
        </p>
        <p>{formatters.number(usagePercentage, 0)}%</p>
      </div>
    </ProgressBarCard>
  )

  return { component, showAlert: usagePercentage > FILE_STORAGE_ALERT_THRESHOLD }
}

export const UploadDocumentStep = ({ close, botId, workspaceId, kbId, footerRef }: UploadDocumentStepProps) => {
  const [uploadableFiles, setUploadableFiles] = useState<File[]>([])
  const [rejectedFiles, setRejectedFiles] = useState<FileRejection[]>([])

  const { mutate: uploadKnowledgeSources, isPending } = useUploadDocumentSources()

  const totalFileSize = uploadableFiles.reduce((total, file) => total + file.size, 0)

  const onDrop = (accepted: File[], fileRejections: FileRejection[]) => {
    setRejectedFiles([])

    if (fileRejections.length > 0) {
      setRejectedFiles((prev) => [...prev, ...fileRejections])
    }

    setUploadableFiles((prev) => uniqBy([...prev, ...accepted], 'name'))
  }

  const removeUploadableFile = (fileToRemove: File) => {
    setUploadableFiles((prev) => prev.filter((file) => file !== fileToRemove))
  }

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject, open } = useDropzone({
    maxSize: MAX_FILE_SIZE,
    accept: ACCEPT,
    onDrop,
  })
  const { component: storageAlertComponent, showAlert } = StorageAlert({ totalFileSize, workspaceId })

  const onConfirm = (e?: React.MouseEvent) => {
    e?.preventDefault()
    e?.stopPropagation()

    uploadKnowledgeSources({
      workspaceId,
      botId,
      kbId,
      files: uploadableFiles,
      onSuccess: () => {
        close()
      },
    })
  }

  const FooterPortal = () => {
    if (!footerRef.current) return null

    return createPortal(
      <AddKnowledgeSourceFooter
        linkName="Learn more about documents for your Botpress agent"
        linkHref="https://botpress.com/docs/learn/reference/knowledge-base/add-sources#documents"
        showCancel={true}
        onClose={close}
        primaryAction={
          <>
            <AsyncButton
              size={'sm'}
              className="min-w-[100px]"
              loading={isPending}
              disabled={uploadableFiles.length === 0 || isPending}
              onClick={onConfirm}
            >{`${uploadableFiles.length} ${uploadableFiles.length > 1 ? 'Uploads' : 'Upload'}`}</AsyncButton>
          </>
        }
      />,
      footerRef.current
    )
  }

  return (
    <>
      <div
        className={cn(
          'flex h-[calc(100vh-300px)] w-full flex-col gap-6 overflow-y-auto @container',
          rejectedFiles.length === 0 && 'h-[30vh]'
        )}
      >
        {showAlert && storageAlertComponent}
        <div className="flex h-full flex-col justify-center gap-x-4 gap-y-6 @lg:grid @lg:grid-cols-2 @lg:grid-rows-2">
          <Button variant="outline" onClick={open} className="flex h-12 items-center @lg:hidden">
            <UploadCloud size={24} />
            <p>Upload Documents</p>
          </Button>
          <div
            {...getRootProps()}
            className={cn(
              'group hidden h-full cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed p-4 text-center transition-colors hover:border-primary/70 @lg:row-span-2 @lg:flex sm:p-8',
              isDragActive ? 'border-primary' : 'border-muted-foreground/30',
              isDragAccept ? 'bg-primary/10' : '',
              isDragReject ? 'border-danger-700 !bg-danger-100' : ''
            )}
          >
            <input {...getInputProps()} />
            <UploadCloud
              className={cn(
                'mb-3 size-8 !stroke-1 transition-colors sm:mb-4 sm:size-12',
                isDragActive ? 'text-primary' : 'text-gray-10',
                'group-hover:text-accent-9'
              )}
            />
            <p className="text-xs text-gray-11 transition-colors group-hover:text-accent-10 sm:text-sm">
              {isDragActive
                ? isDragAccept
                  ? 'Drop files here'
                  : 'Some files may be rejected'
                : "Drag 'n' drop files here, or click to select"}
            </p>
          </div>
          {rejectedFiles.length > 0 || uploadableFiles.length > 0 ? (
            <>
              <Card className={cn('flex h-full flex-col', rejectedFiles.length === 0 && '@lg:row-span-2')}>
                <CardHeader className="overflow-hidden rounded-t-lg border-b bg-muted p-4">
                  <CardTitle>Upload Documents</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-1 flex-col overflow-y-auto rounded-b-lg p-0">
                  {uploadableFiles.map((file, index) => (
                    <div key={file.name}>
                      <div className="flex items-center justify-between gap-2 py-2 pl-2 hover:bg-muted">
                        <div className="flex max-w-3/4 items-center gap-2">
                          <Icon icon={getFileIcon(file)} size="4" />
                          <p className="truncate text-sm font-medium">{file.name}</p>
                          <p className="text-nowrap text-sm text-muted-foreground">{`(${formatters.byte(file.size)})`}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeUploadableFile(file)}
                          className="flex-shrink-0"
                        >
                          <Trash size={16} color="red" />
                        </Button>
                      </div>
                      {index !== uploadableFiles.length - 1 && <Separator />}
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card className={cn('flex h-full flex-col', rejectedFiles.length === 0 && 'hidden')}>
                <CardHeader className="rounded-t-lg border-b bg-muted p-4">
                  <CardTitle>Rejected Files</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-1 flex-col overflow-y-auto rounded-b-lg p-0">
                  {rejectedFiles.map(({ file, errors }, index) => (
                    <div key={file.name}>
                      <div className=" flex flex-col items-start justify-between gap-2 p-2 hover:bg-muted">
                        <div className="flex max-w-full items-center gap-2">
                          <Icon icon={getFileIcon(file)} size="4" />
                          <p className="truncate text-sm font-medium">{file.name}</p>
                          <p className="text-nowrap text-sm text-muted-foreground">{`(${formatters.byte(file.size)})`}</p>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-danger">
                          {errors.length === 1 ? (
                            <>
                              <AlertTriangle size={16} className="flex-shrink-0 text-danger" />
                              <span>{formatErrorMessage(errors[0]!)}</span>
                            </>
                          ) : (
                            <ul className="flex list-inside list-none flex-col gap-2">
                              {errors.map((error) => (
                                <li key={error.code} className="flex gap-2">
                                  <AlertTriangle size={16} className="flex-shrink-0 text-danger" />
                                  {formatErrorMessage(error)}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                      {index !== rejectedFiles.length - 1 && <Separator />}
                    </div>
                  ))}
                </CardContent>
              </Card>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-lg bg-muted p-4 text-sm text-muted-foreground @lg:row-span-2">
              <div className="mb-4 grid grid-cols-3 gap-2">
                <Icon icon={pdfIcon} size="6" />
                <Icon icon={txtIcon} size="6" />
                <Icon icon={docIcon} size="6" />
                <Icon icon={docxIcon} size="6" />
                <Icon icon={htmlIcon} size="6" />
                <Icon icon={mdIcon} size="6" />
              </div>
              <h2 className="font-medium">Supported formats</h2>
              <p className="">.pdf, .txt, .docx, .html, .md</p>
              <p>{`(Max file size ${formatters.byte(MAX_FILE_SIZE)})`}</p>
            </div>
          )}
        </div>
      </div>
      <FooterPortal />
    </>
  )
}

const formatErrorMessage = (error: { code: string; message: string }): string => {
  switch (error.code) {
    case 'file-invalid-type':
      return 'File type not supported. Please upload .pdf, .txt, .doc, .docx, .html or .md files.'
    case 'file-too-large':
      return 'File is too large. Please choose a smaller file.'
    case 'file-too-small':
      return 'File is too small. Please choose a larger file.'
    case 'too-many-files':
      return 'Too many files. Please select fewer files.'
    default:
      return error.message
  }
}

const getFileIcon = (file: File) => {
  return match(getContentType(file))
    .with('application/pdf', () => pdfIcon)
    .with('text/html', () => htmlIcon)
    .with('text/plain', () => txtIcon)
    .with('application/msword', () => docIcon)
    .with('application/vnd.openxmlformats-officedocument.wordprocessingml.document', () => docxIcon)
    .with('text/markdown', () => mdIcon)
    .otherwise(() => FileIcon)
}
